import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { computed } from '@ember/object';
import { reads } from '@ember/object/computed';
import { addObserver, removeObserver } from '@ember/object/observers';
import { inject as service } from '@ember/service';

import FunctionalUtils from 'mewe/shared/functional-utils';
import PS from 'mewe/utils/pubsub';
import ChatApi from 'mewe/api/chat-api';
import GroupStore from 'mewe/stores/group-store';
import ChatStore from 'mewe/stores/chat-store';
import { next } from '@ember/runloop';

export default class MwGroupSettingsChat extends Component {
  @reads('args.group') group;

  @service dynamicDialogs;
  @service account;

  @tracked loaded = false;
  @tracked chatMode = '';
  @tracked isSavingChatMode = false;
  @tracked isDeletingHistory = false;

  constructor() {
    super(...arguments);

    if (this.group && !this.group.isFetching) {
      this.groupDataReady();
    }

    addObserver(this, 'group.isFetching', this, this.groupDataReady);
  }

  willDestroy() {
    removeObserver(this, 'group.isFetching', this, this.groupDataReady);
  }

  groupDataReady() {
    if (!this.loaded && this.group?.id && !this.group.isFetching) {
      next(() => {
        this.loaded = true;
      });
      /**
       * chatMode stored in separate variable
       * so it can be later compared with saved value
       */
      this.chatMode = this.group.chatMode;
    }
  }

  @computed('chatMode', 'isSavingChatMode')
  get isSaveVisible() {
    const isChatVisibilityChanged = this.chatMode !== this.group.chatMode;
    return isChatVisibilityChanged && !this.isSavingChatMode;
  }

  @action
  showDeleteConfirmation() {
    this.dynamicDialogs.openDialog('confirm-deletion-dialog', {
      onConfirm: () => this.submitDeleting(),
    });
  }

  @action
  chatModeChange(chatMode) {
    this.chatMode = chatMode;
  }

  @action
  saveChanges() {
    const chatMode = this.chatMode;
    const groupId = this.group.id;

    this.isSavingChatMode = true;

    ChatApi.setGroupChatMode(groupId, { chatMode: chatMode })
      .then(() => {
        FunctionalUtils.info(__('Chat Settings have been successfully saved.'));

        let group = GroupStore.getState({ id: groupId });
        group.set('chatMode', chatMode);
        group.set('hiddenChat', chatMode === 'off');

        if (chatMode === 'off') {
          ChatStore.send('removeThreadById', groupId);
        }
      })
      .catch(() => {
        this.chatMode = this.group.chatMode;
        FunctionalUtils.showDefaultErrorMessage();
      })
      .finally(() => {
        this.isSavingChatMode = false;
      });
  }

  @action
  submitDeleting() {
    this.isDeletingHistory = true;

    // delete group chat history form
    ChatApi.deleteGroupChatHistory(this.group.id)
      .then(() => {
        FunctionalUtils.info(__('Group chat history has been successfully deleted.'));
        PS.Pub('chat.remove.all.messages', {
          threadId: this.group.id,
        });
      })
      .catch(() => {
        FunctionalUtils.showDefaultErrorMessage();
      })
      .finally(() => {
        this.isDeletingHistory = false;
      });
  }
}
